import { format } from 'date-fns';
import { ref, update } from 'firebase/database';
import { Form, FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import { Input } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import { commentApi } from 'api';
import { database } from 'Common/Firebase';
import { ICL, Task, TaskComment } from 'shared/types';

import arrowLeftButton from '../../../assets/img/arrow-left.svg';
import checkSvg from '../../../assets/img/check_icon.svg';
import pen from '../../../assets/img/pen.svg';
import send from '../../../assets/img/send.svg';
import { getUser, getUserType } from '../../../redux/slices/userSlice';
import { commentValidationSchema } from '../../helpers/comment-validation';
import { useModal } from '../../hooks';
import { ItemActionModal } from '../ItemActionModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  icl: ICL | null;
  taskId: Task['id'] | null;
  hw?: boolean;
}

export const CommentModal = ({ isOpen, onClose, icl, taskId, hw }: Props) => {
  const { t } = useTranslation();
  const {
    isOpen: isDeletingCommentModalOpen,
    close: closeDeletingCommentModal,
    open: openDeletingCommentModal,
  } = useModal();

  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [comment, setComment] = useState<TaskComment>();
  const [comments, setComments] = useState<TaskComment[]>();
  const [commentId, setCommentId] = useState<string>();
  const [deleteCommentId, setDeleteCommentId] = useState<string>();
  const isEditingForm = !!commentId;
  const { id: iclId, subject, studentEmail } = icl as ICL;
  const areRequiredIds = isEditingForm ? iclId && taskId && commentId : iclId && taskId;
  const userTypeSelector = useSelector(getUserType);
  const userSelector = useSelector(getUser);

  useEffect(() => {
    if (!iclId || !taskId || !commentId || !isEditingForm) {
      return;
    }

    const setNewComment = (newComment: TaskComment) => setComment(newComment);
    const unsubscribe = commentApi.subscribeToComment({ iclId, taskId, commentId, setComment: setNewComment });

    setIsLoading(false);

    return () => {
      unsubscribe();
    };
  }, [commentId]);

  useEffect(() => {
    if (!iclId || !taskId) {
      return;
    }

    const setGottenComments = (gottenComments: TaskComment[]) => setComments(gottenComments);
    const unsubscribe = commentApi.subscribeToComments({ iclId, taskId, setComments: setGottenComments });
    setIsLoading(false);

    return () => {
      unsubscribe();
    };
  }, [iclId]);

  const formik = useFormik({
    initialValues: {
      text: (isEditingForm && comment?.text) || '',
    },
    validationSchema: commentValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      if (!iclId || !taskId) {
        return;
      }

      setIsLoading(true);

      try {
        isEditingForm
          ? comment && (await commentApi.updateComment(iclId, taskId, comment.id, { text: values.text }))
          : await commentApi.addComment(
              iclId,
              taskId,
              values.text,
              userSelector?.patronymic
                ? `${userSelector?.firstName} ${userSelector?.patronymic}`
                : `${userSelector?.firstName} ${userSelector?.lastName}`,
            );
        toast.success(t(isEditingForm ? t('Successfully edited!') : t('Successfully added!')));

        const now = new Date();
        const formattedTime = format(now, 'dd MM yyyy HH:mm');
        const notificationId = uuidv4();

        const notificationData = {
          id: notificationId + studentEmail,
          idItem: studentEmail,
          subject,
          name: '',
          time: formattedTime,
          type: hw ? 'commentHw' : 'comment',
          iclId,
          viewed: true,
        };

        await update(ref(database, `Students/${studentEmail}/notification`), {
          [notificationId + studentEmail]: notificationData,
        });

        resetForm();

        setComment(undefined);
        setCommentId(undefined);
      } catch (error) {
        console.log(error);
        toast.error(t('Something went wrong. Try again!'));
      }

      setIsLoading(false);
    },
  });

  const hasFormikErrors = !!Object.keys(formik.errors).length;
  const isSubmitButtonDisabled = hasFormikErrors || isLoading || !areRequiredIds;

  const removeComment = async () => {
    if (!iclId || !deleteCommentId || !taskId) {
      return;
    }

    setIsActionLoading(true);

    try {
      await commentApi.removeComment(iclId, taskId, deleteCommentId);
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsActionLoading(false);
    closeDeletingCommentModal();
    toast.success(t('comment remove'));
  };

  const openRemovingCommentModal = (commentId: string) => {
    setDeleteCommentId(commentId);
    openDeletingCommentModal();
  };

  const openEditingCommentModal = (commentId: string) => {
    setCommentId(commentId);
  };

  const markCommentWasViewed = async (commentId: string, isViewed: boolean) => {
    if (!iclId || !taskId) {
      return;
    }

    setIsActionLoading(true);

    try {
      await commentApi.updateComment(iclId, taskId, commentId, { isViewed });
      toast.success(t('Successfully edited!'));
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsActionLoading(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="relative bottom-auto mx-auto h-[90%] w-full translate-y-[10vh] rounded-t-[24px] bg-[#FAF8F4] p-[16px] lg:w-[520px]"
      overlayClassName="w-full fixed top-0 left-0 rigth-0 bottom-0 bg-[#0d0d0ccc]"
      closeTimeoutMS={300}
    >
      <div className="relative mb-[30px] flex items-center justify-center gap-[80px]">
        <div
          onClick={onClose}
          className="absolute left-0 flex h-[32px] w-[32px] items-center justify-center rounded-[10px] border-[1px] border-[#E8EBEC]"
        >
          <img src={arrowLeftButton} alt="arrow left" />
        </div>

        <div className="text-[16px] font-bold">{t('comments')}</div>
      </div>

      {comments && (
        <div>
          <ul>
            {comments.map(comment => (
              <li key={comment.id}>
                <div className="mt-[5px] flex flex-col items-center">
                  <div className="w-full rounded-[10px] border-[1px] border-[#52C7FA] bg-[#e7f5fb] px-[12px] py-[8px]">
                    <div className="flex items-center justify-between text-[14px]">
                      <div className="font-bold">{comment.name}</div>

                      <div className="text-[#656D7B]">{moment(comment.createdAt).format('HH:mm')}</div>
                    </div>

                    <div className="text-[14px] text-[#242C2C]">{comment.text}</div>

                    {comment.isViewed && userTypeSelector === 'Teacher' && (
                      <div className="mt-[10px] flex text-[12px] text-[#178567]">
                        <img src={checkSvg} alt={'check'} className="mr-[4px]" /> {t('Viewed')}
                      </div>
                    )}
                  </div>

                  <div className="mt-[10px] flex w-full items-center justify-between">
                    {userTypeSelector === 'Teacher' && (
                      <div className="flex gap-[19px]">
                        <button
                          className="flex items-center text-[14px]"
                          onClick={() => openEditingCommentModal(comment.id)}
                        >
                          <img className="mr-[8px] h-[16px] w-[16px]" src={pen} alt="pen" /> <div>{t('update')}</div>
                        </button>
                        <button
                          className="flex items-center text-[14px]"
                          onClick={() => openRemovingCommentModal(comment.id)}
                        >
                          <span className="icon icon-delete mr-[8px] block h-[16px] w-[16px] text-red-600" />
                          <div>{t('delete')}</div>
                        </button>
                      </div>
                    )}

                    {userTypeSelector === 'Student' && (
                      <div className="mb-[10px] flex items-center">
                        <Input
                          type="checkbox"
                          className="mr-[12px] mt-0 h-[20px] w-[20px] border-2 border-[#08B781] checked:border-[#08B781] checked:bg-[#08B781] checked:ring-[#08B781] checked:ring-opacity-50 focus:border-[#08B781] focus:ring-[#08B781] focus:ring-opacity-50"
                          checked={comment.isViewed}
                          onChange={() => markCommentWasViewed(comment.id, !comment.isViewed)}
                          disabled={!(userTypeSelector === 'Student')}
                        />

                        <div className="text-[12px]">{t('Viewed')}</div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {userTypeSelector === 'Teacher' && (
        <FormikProvider value={formik}>
          <Form className="fixed bottom-0 left-0 w-full bg-white p-[20px]">
            <div className="flex w-full gap-4 rounded-[32px] border-[1px] border-[#d1cec7] pb-[8px] pl-[20px] pr-[6px] pt-[6px]">
              <input
                id="text"
                className="w-[80%] border-0 lg:w-[86%]"
                value={formik.values.text}
                onChange={event => formik.setFieldValue('text', event.target.value)}
                placeholder={t('add comment')}
              />

              <button
                className="flex h-[36px] w-[36px] items-center justify-center rounded-full bg-[#52C7FA]"
                type="submit"
                disabled={isSubmitButtonDisabled}
              >
                <img src={send} alt={'send'} />
              </button>
            </div>
          </Form>
        </FormikProvider>
      )}

      <ItemActionModal
        isOpen={isDeletingCommentModalOpen}
        close={closeDeletingCommentModal}
        title={t('Removing Comment Modal')}
        onHandle={removeComment}
        isSubmitButtonDisabled={isActionLoading}
        buttonTitle={t('comment removed')}
      />
    </Modal>
  );
};
